/* eslint-disable import/no-named-as-default-member -- react boot */
/* eslint-disable @typescript-eslint/no-non-null-assertion -- react boot */
// Esse é um comentário importante. Leia

// ⚠️ IMPORTANTE: Importar polyfills ANTES de qualquer outro código para garantir compatibilidade
import "@/utils/polyfills";

import { StytchProvider } from "@stytch/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  createRouter,
  type ErrorComponentProps,
  type RegisteredRouter,
  RouterProvider,
} from "@tanstack/react-router";
import { getDefaultStore } from "jotai";
import { RESET } from "jotai/utils";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { lazy, StrictMode, Suspense, useMemo, type JSX } from "react";
import ReactDOM from "react-dom/client";
import { ZendeskProvider } from "react-use-zendesk";

import { ErrorPage, NotFoundPage } from "@/components/ui/layout/page/error-page";
import { BootGate } from "@/components/ui/loading/boot-gate";
import {
  batchAdjustFormAtom,
  batchAdjustAppliedIdsAtom,
  batchAdjustOriginAtom,
  batchAdjustTargetsAtom,
} from "@/lib/atoms/batch-adjust-form-atom";
import { initAutoUpdate } from "@/lib/auto-updater";
import { initDeepLink } from "@/lib/deep-link.ts";
import {
  DefaultFeatureFlagsValue,
  type FeatureFlags,
  LaunchDarklyClient,
  withDefaultLDProvider,
} from "@/lib/launchdarkly.ts";
import { setStatusBarStyle } from "@/lib/status-bar";
import { stytchClient } from "@/lib/stytch";
import { trackPageView } from "@/lib/track-page-view";
import { monitorRudderStackLoad } from "@/lib/tracking";
import { ENVIRONMENT, VITE_ZENDESK_SNIPPET_KEY } from "@/lib/utils/config";
import { initSentry } from "@/lib/utils/sentry";
import { routeTree } from "@/route-tree.gen";
import { initialRootContext } from "@/routes/-root/-root-context";

initAutoUpdate();

const router = createRouter({
  routeTree,
  defaultErrorComponent: (props: ErrorComponentProps) => (
    <ErrorPage error={props.error} />
  ),
  context: initialRootContext,
  defaultNotFoundComponent: () => <NotFoundPage />,
  defaultPreload: "intent",
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
});

router.subscribe("onLoad", ({ fromLocation, toLocation, pathChanged }) => {
  if (pathChanged) {
    const store = getDefaultStore();
    store.set(batchAdjustFormAtom, RESET);
    if (!toLocation.pathname.startsWith("/calendar/batch-adjust")) {
      store.set(batchAdjustTargetsAtom, RESET);
      store.set(batchAdjustOriginAtom, RESET);
      store.set(batchAdjustAppliedIdsAtom, RESET);
    }
    window.userpilot?.reload();
    trackPageView(toLocation.pathname, { from: fromLocation?.pathname ?? "" });
  }
});

// Detecta, uma vez por sessão, se o SDK do RudderStack não carregou (bloqueio de
// rede/CDN) e reporta ao Sentry — que chega mesmo em devices com analytics
// bloqueado. Encadeado ao initSentry para o timeout de 10s só começar com o client
// do Sentry já pronto; senão um init lento descartaria o próprio log. Ver CBR-3937.
void initSentry().then(monitorRudderStackLoad);
void initDeepLink(router);
void setStatusBarStyle();

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Fora do componente: um `lazy` criado no render vira um componente novo a cada render e
// remonta o devtools, que lança erro ao remontar e, no React 19, derruba a árvore inteira.
const TanStackRouterDevtools =
  ENVIRONMENT === "production"
    ? () => null // Render nothing in production
    : lazy(() =>
        // Lazy load in development
        import("@tanstack/react-router-devtools").then((res) => ({
          default: res.TanStackRouterDevtools,
        })),
      );

export const DevMode = (props: { router: RegisteredRouter }): JSX.Element => {
  return (
    <Suspense>
      <TanStackRouterDevtools position="top-right" router={props.router} />
      <ReactQueryDevtools buttonPosition="bottom-left" />
    </Suspense>
  );
};

const App = (): JSX.Element => {
  const ldClient = useLDClient();
  const flags = useFlags<FeatureFlags>();

  const localLaunchDarklyClient = useMemo(
    () => (ldClient ? new LaunchDarklyClient(ldClient) : undefined),
    [ldClient],
  );

  // Espera até que o ldClient esteja disponível para evitar flick. Enquanto isso o
  // BootGate mantém o splash nativo e o Loading (logo + pontinhos) na tela.
  return (
    <BootGate router={router}>
      {localLaunchDarklyClient && (
        <StytchProvider stytch={stytchClient}>
          <QueryClientProvider client={initialRootContext.queryClient}>
            <ZendeskProvider apiKey={VITE_ZENDESK_SNIPPET_KEY}>
              <RouterProvider
                router={router}
                context={{
                  ...initialRootContext,
                  ldClient: localLaunchDarklyClient,
                  flags: Object.values(flags).length ? flags : DefaultFeatureFlagsValue,
                }}
              />
              <DevMode router={router} />
            </ZendeskProvider>
          </QueryClientProvider>
        </StytchProvider>
      )}
    </BootGate>
  );
};

const AppWithLaunchDarkly = withDefaultLDProvider(App);

// Render the app
// biome-ignore lint/style/noNonNullAssertion: legacy code
const rootElement = document.getElementById("app")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <AppWithLaunchDarkly />
    </StrictMode>,
  );
}

// Cache buster
